/**
 * Historia wersji Sianko widoczna w Settings → klik w "Sianko · v...".
 *
 * Jeden wpis = jedna wersja. Wewnątrz wpisu może być jedna lub więcej
 * sekcji rozdzielonych separatorem w UI:
 *   - "feature"  — nowa funkcja / ulepszenie. Bullet ✨.
 *   - "bugfix"   — poprawka błędu. Bullet 🔧.
 *   - "security" — poprawka bezpieczeństwa. Bullet 🔒. Bez listy zmian;
 *                  UI pokaże stałe zdanie „Szczegóły nieujawnione…".
 *
 * Dodawaj nowy wpis na samej górze tablicy przy każdym deployu, który
 * zmienia coś widocznego dla użytkownika. Wersja w `version` powinna
 * pasować do `package.json` w momencie deployu.
 */

export type ChangelogCategory = "feature" | "bugfix" | "security";

export type ChangelogSection = {
  kind: ChangelogCategory;
  /** Pomijane dla `security` (UI pokazuje stałe zdanie). */
  items?: string[];
};

export type ChangelogEntry = {
  version: string;
  /** ISO date (YYYY-MM-DD). */
  date: string;
  sections: ChangelogSection[];
};

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: "1.50.0",
    date: "2026-08-06",
    sections: [
      {
        kind: "feature",
        items: [
          "Na pulpicie dziecka pojawiła się Polana Sianka — kolorowy świat, który rośnie razem z kolekcją gadżetów.",
          "Pierwsze zatwierdzone zdjęcie zadania każdego dnia daje klucz. Trzy klucze otwierają skrzynkę.",
          "Każda skrzynka losuje jeden nowy gadżet bez duplikatów: towarzysza, plecak, okulary, bluzę, łańcuch, fryzurę, magiczną aurę albo nowe tło pokoju.",
          "W garderobie czeka 35 rzeczy, w tym lisek, kotek, piesek, kura, tygrys, plecaki podróżne, niezwykłe okulary i pięć różnych pokoi.",
          "Zdobyte przedmioty można dowolnie zakładać w garderobie, a ubrany bohater czeka na dziecko na jego pulpicie.",
        ],
      },
    ],
  },
  {
    version: "1.49.1",
    date: "2026-06-11",
    sections: [
      {
        kind: "bugfix",
        items: [
          "Okno wypłaty wygląda teraz spójnie z resztą aplikacji, a wybór „na co idą pieniądze” jest czytelniejszy.",
          "Gdy włączasz dopłatę 50/50 na celu dziecka, cena podpowiada się z kwoty, którą dziecko już ustawiło — nie musisz wpisywać jej od nowa.",
        ],
      },
    ],
  },
  {
    version: "1.49.0",
    date: "2026-06-11",
    sections: [
      {
        kind: "feature",
        items: [
          "Cel dziecka widać teraz w Twojej skarbonce. Gdy dziecko ustawi cel oszczędzania, pokazuje się przy jego saldzie razem z paskiem postępu.",
          "Stąd jednym kliknięciem ustawisz dopłatę 50/50 — dziecko zbiera swoją część, a Ty dokładasz resztę przy zakupie. Wcześniej trzeba było szukać tego w historii dziecka.",
        ],
      },
    ],
  },
  {
    version: "1.48.0",
    date: "2026-06-11",
    sections: [
      {
        kind: "feature",
        items: [
          "Nowy Regulamin i nowa Polityka Prywatności — pełne, przygotowane z prawnikiem wersje dokumentów. Znajdziesz je w Ustawieniach oraz pod adresami /terms i /privacy.",
          "Dokumenty opisują dokładnie, jak działa Sianko: zdjęcia zadań nigdy nie trafiają do AI, konto dziecka nie wymaga adresu e-mail, a dane są przechowywane w Europie.",
        ],
      },
    ],
  },
  {
    version: "1.47.0",
    date: "2026-06-11",
    sections: [
      {
        kind: "feature",
        items: [
          "Checklista „Pierwszy tydzień z Sianko” na pulpicie rodzica — pięć kroków od dodania dziecka do pierwszej wypłaty. Znika, gdy wszystko odhaczone.",
          "Jednym z kroków jest instalacja Sianko na telefonie — z ikoną na ekranie i powiadomieniami o zgłoszeniach.",
        ],
      },
    ],
  },
  {
    version: "1.46.0",
    date: "2026-06-11",
    sections: [
      {
        kind: "feature",
        items: [
          "Kod QR loguje teraz dziecko od razu — bez wpisywania hasła. Dziecko skanuje kod zwykłym aparatem i już jest w Sianko. Kod działa godzinę.",
          "Kod logowania znajdziesz też w Ustawienia → Domownicy → dziecko — przyda się przy nowym telefonie.",
        ],
      },
    ],
  },
  {
    version: "1.45.0",
    date: "2026-06-11",
    sections: [
      {
        kind: "feature",
        items: [
          "Po dodaniu dziecka zobaczysz kartę logowania z kodem QR. Dziecko skanuje go aparatem — rodzina i nazwa wpisują się same, zostaje tylko hasło.",
          "Przycisk „Wygeneruj proste hasło” przy dodawaniu dziecka: łatwe słowo z cyframi, które dziecko bez problemu wpisze.",
          "Rodzic może teraz tworzyć i edytować cele oszczędzania dziecka w jego portfelu — w tym włączać zasadę 50/50.",
        ],
      },
    ],
  },
  {
    version: "1.44.0",
    date: "2026-06-11",
    sections: [
      {
        kind: "feature",
        items: [
          "Zasada 50/50 przy celach: dziecko zbiera swoją część ceny, a rodzic dokłada resztę przy zakupie. Włączysz ją przy tworzeniu lub edycji celu.",
          "Cel z zasadą 50/50 pokazuje pełną cenę rzeczy i część do uzbierania. Gdy część dziecka jest gotowa — czas na wspólny zakup! 🤝",
        ],
      },
    ],
  },
  {
    version: "1.43.0",
    date: "2026-06-11",
    sections: [
      {
        kind: "feature",
        items: [
          "Przy wypłacie możesz zaznaczyć, na co idą pieniądze: 🎯 na cel, 🛍️ do wydania albo 🐷 odkładane. Widać to potem w historii portfela.",
          "Pytanie tygodnia pasuje teraz do wieku najmłodszego dziecka i co tydzień prowadzi Was przez inny temat rozmów o pieniądzach.",
          "Nowe pytania do rodzinnych rozmów — osobne dla młodszych (6–8 lat) i starszych (12+) dzieci.",
          "„OK, pogadamy” zapisuje się dla całej rodziny na wszystkich urządzeniach. Po odhaczeniu widać, ile rozmów odbyliście w tym roku.",
        ],
      },
    ],
  },
  {
    version: "1.42.2",
    date: "2026-06-08",
    sections: [
      {
        kind: "bugfix",
        items: [
          "Szybkie zgłoszenia dziecka pojawiają się teraz u rodzica w kolejce „Do zatwierdzenia” jako „wykonane od razu”.",
          "Licznik przy zakładce „Sprawdź” pokazuje też szybkie zgłoszenia i propozycje zadań.",
        ],
      },
    ],
  },
  {
    version: "1.42.1",
    date: "2026-06-08",
    sections: [
      {
        kind: "bugfix",
        items: [
          "Poprawiliśmy zgłaszanie zadań ze zdjęciem u dziecka — zdjęcie przygotowuje się stabilniej w aplikacji na telefonie.",
          "Zadania dziecka nie pojawiają się już za wcześnie podczas ładowania konta.",
        ],
      },
    ],
  },
  {
    version: "1.42.0",
    date: "2026-05-27",
    sections: [
      {
        kind: "feature",
        items: [
          "Nowy typ zadania: 💪 wyzwanie sportowe — z licznikiem powtórzeń (pompki, brzuszki) lub minut (bieg, rower, piłka, koszykówka).",
          "Presety dyscyplin do wyboru jednym kliknięciem — albo wpisz własną i pozwól Sianko AI dobrać ikonę, jednostkę i sensowny cel.",
          "Opcjonalna auto-progresja celu: ustaw „podbijaj cel co tydzień o N” — cel rośnie sam przy zatwierdzeniu wykonania (raz w tygodniu, nie częściej).",
          "Rekord życiowy 🏆 widoczny obok celu — pokazuje najwięcej, co dziecko zrobiło dla tego wyzwania. Bez fajerwerków, po prostu wartość.",
        ],
      },
    ],
  },
  {
    version: "1.40.1",
    date: "2026-05-25",
    sections: [
      {
        kind: "bugfix",
        items: [
          "Poprawka modalu „Co nowego w Sianko” — pierwsza wersja pokazywała się tylko nowym osobom; teraz pokazuje się wszystkim po aktualizacji aplikacji.",
        ],
      },
    ],
  },
  {
    version: "1.40.0",
    date: "2026-05-25",
    sections: [
      {
        kind: "feature",
        items: [
          "Po każdej aktualizacji aplikacji widzisz automatycznie krótki modal „Co nowego w Sianko” z listą zmian od Twojej ostatniej wizyty — żebyś nie musiał szukać w Ustawieniach co się zmieniło. Nowi użytkownicy go nie widzą (welcome flow ich wita).",
        ],
      },
    ],
  },
  {
    version: "1.39.0",
    date: "2026-05-25",
    sections: [
      {
        kind: "feature",
        items: [
          "Nowy przycisk „⚡ Zgłoś” w Zadaniach dla dziecka — gdy zrobi coś poza listą (np. skosi trawę albo pomoże babci), wstawia zdjęcie + tytuł + (jeśli płatne) stawkę i rodzic jednym kliknięciem zatwierdza całość. Wcześniej trzeba było robić to w dwóch krokach: zaproponować zadanie, potem zgłosić wykonanie.",
          "Zdjęcie jest wymagane, max 100 zł, max 10 zgłoszeń dziennie — żeby nie było nadużyć.",
        ],
      },
    ],
  },
  {
    version: "1.38.0",
    date: "2026-05-24",
    sections: [
      {
        kind: "feature",
        items: [
          "Cel rodziny po osiągnięciu zostaje widoczny jeszcze przez 5 dni z banderolą „🎉 Cel osiągnięty!” — żeby każdy w rodzinie zobaczył co razem wykręciliście. Pasek na 100%, lista contributorów (kto ile dorzucił), bez powiadomień push.",
        ],
      },
    ],
  },
  {
    version: "1.37.0",
    date: "2026-05-24",
    sections: [
      {
        kind: "feature",
        items: [
          "W Ustawieniach → Rodzina pod imieniem każdego dziecka widać teraz jego poziom, rangę i punkty doświadczenia (np. „📦 Lvl 4 · Kolekcjoner Sianka · 420 XP”). Dzięki temu wszyscy w rodzinie wiedzą jak komu idzie — bez pytania.",
        ],
      },
    ],
  },
  {
    version: "1.36.0",
    date: "2026-05-24",
    sections: [
      {
        kind: "feature",
        items: [
          "Asysta AI przy tworzeniu zadań (ikona, poprawiona nazwa, klasyfikacja trudności) korzysta teraz z Google Gemini zamiast Claude. Funkcja działa tak samo — zmiana dotyczy tylko dostawcy.",
          "Doprecyzowaliśmy Politykę Prywatności i Regulamin: zdjęcia-dowody nigdy nie są przesyłane do AI. Ogląda je wyłącznie rodzic w aplikacji.",
        ],
      },
    ],
  },
  {
    version: "1.35.0",
    date: "2026-05-13",
    sections: [
      {
        kind: "feature",
        items: [
          "Po rejestracji rodzica wysyłamy link weryfikacyjny na email i czekamy aż klikniesz — dopiero potem zakładamy rodzinę. Jak klikniesz link w mailu, aplikacja automatycznie ruszy dalej. Dzięki temu nikt nie zakłada konta na cudzy adres.",
        ],
      },
      { kind: "security" },
    ],
  },
  {
    version: "1.34.1",
    date: "2026-05-13",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.34.0",
    date: "2026-05-12",
    sections: [
      {
        kind: "feature",
        items: [
          "Presety zadań przy dodawaniu nowego dziecka — po założeniu konta proponujemy gotowy zestaw 5-7 chore'ów dopasowany do wieku (6-8 / 9-11 / 12-14 lat) plus uniwersalny zestaw „Czas poza ekranem”. Dashboard dziecka startuje z czymś sensownym, można odznaczyć poszczególne zadania lub pominąć cały krok.",
          "Dodaj dziecko: nowe opcjonalne pole „Wiek” — pomaga dobrać preset. Zapis jako rok urodzenia, więc dane nie starzeją się z czasem.",
          "Nowa opcja „Dzieci” przy tworzeniu zadania — jedno kliknięcie przypisuje to samo zadanie wszystkim dzieciom naraz (np. czytanie, sport, spacer z psem). Każde dziecko widzi je u siebie i zgłasza osobno.",
          "Doprecyzowanie copy filozofii: „W rodzinie nie ma pensji. Codzienne obowiązki robimy, bo jesteśmy rodziną — bez stawki. Za zadania dodatkowe rodzic może dodać kwotę albo zostawić je bez płatności.” — żeby nie brzmiało jakby aplikacja sama płaciła pieniądze.",
        ],
      },
    ],
  },
  {
    version: "1.33.2",
    date: "2026-05-12",
    sections: [
      {
        kind: "feature",
        items: [
          "Karty filozofii Sianko (w powitaniu rodzica, w pickerze pierwszego zadania i w Ustawieniach) zaktualizowane zgodnie z nowym wpisem na blogu: Podstawowa higiena (poza aplikacją), Pomoc rodzinie (XP, bez kasy), Zadania dodatkowe (opcjonalnie płatne).",
        ],
      },
    ],
  },
  {
    version: "1.33.1",
    date: "2026-05-12",
    sections: [
      {
        kind: "bugfix",
        items: [
          "Czerwona liczba na ikonie aplikacji u dziecka — pokazuje, ile zadań jeszcze zostało na dziś. Wcześniej była zawsze pusta.",
        ],
      },
    ],
  },
  {
    version: "1.33.0",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.32.2",
    date: "2026-05-12",
    sections: [
      {
        kind: "feature",
        items: [
          "Ekran Trzy typy zadań w powitaniu rodzica dostał obrazek całej rodziny Sianko i lepiej mieści się na ekranie telefonu — bez przewijania.",
        ],
      },
    ],
  },
  {
    version: "1.32.1",
    date: "2026-05-12",
    sections: [
      {
        kind: "feature",
        items: [
          "Karta zadania ma czytelną naklejkę w rogu ikony — żółte serduszko dla pomocy rodzinie, worek dla zadań dodatkowych, drzewko dla aktywności poza ekranem. Łatwiej jednym spojrzeniem zobaczyć typ zadania.",
          "Przycisk Usuń moje konto w Ustawieniach jest teraz wyraźnie czerwony — od razu widać, że to akcja destrukcyjna, nie zwykły wylog.",
        ],
      },
    ],
  },
  {
    version: "1.32.0",
    date: "2026-05-12",
    sections: [
      {
        kind: "feature",
        items: [
          "Mama i Tata Sianko mają nową, dłuższą lekcję na powitanie rodzica — z trzema kartami pokazującymi, które zadania są płatne, a które nie.",
          "Pierwsze dodawanie zadania prowadzi przez krótki wybór typu — Pomoc rodzinie (XP, bez kasy) albo Zadanie dodatkowe (PLN). Higiena dziecka świadomie nie wchodzi do aplikacji.",
          "Nowa sekcja Filozofia Sianko w Ustawieniach — szybki powrót do trzech typów zadań i link do pełnego wpisu na blogu.",
        ],
      },
    ],
  },
  {
    version: "1.31.0",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.30.2",
    date: "2026-05-12",
    sections: [{ kind: "bugfix" }],
  },
  {
    version: "1.30.1",
    date: "2026-05-12",
    sections: [{ kind: "bugfix" }],
  },
  {
    version: "1.30.0",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.29.3",
    date: "2026-05-12",
    sections: [{ kind: "bugfix" }],
  },
  {
    version: "1.29.2",
    date: "2026-05-12",
    sections: [
      {
        kind: "bugfix",
        items: [
          "Naprawione logowanie przez Google i monitorowanie błędów Sentry — CSP blokował niezbędne domeny.",
        ],
      },
    ],
  },
  {
    version: "1.29.1",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.29.0",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.28.1",
    date: "2026-05-12",
    sections: [
      {
        kind: "bugfix",
        items: [
          "Naprawione wyświetlanie tej historii zmian — wpis 1.28.0 miał błąd składni który blokował aktualizację.",
        ],
      },
    ],
  },
  {
    version: "1.28.0",
    date: "2026-05-12",
    sections: [
      {
        kind: "feature",
        items: [
          "Każdy może usunąć swoje konto w Ustawieniach (nowy przycisk „Usuń moje konto”). Saldo musi być zerowe — jeśli masz niewypłacone, najpierw poproś rodzica o wypłatę. Historia zadań i transakcji zachowuje się jako audyt rodzinny.",
        ],
      },
    ],
  },
  {
    version: "1.27.0",
    date: "2026-05-12",
    sections: [
      {
        kind: "feature",
        items: [
          "AI weryfikacja zdjęć-dowodów została całkowicie wyłączona. Parent zawsze sam ocenia czy zdjęcie pasuje do zadania — bez podpowiedzi AI. Zdjęcia nadal są wymagane gdy chore tego potrzebuje, są chronione krótkotrwałymi linkami.",
        ],
      },
    ],
  },
  {
    version: "1.26.4",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.26.3",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.26.2",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.26.1",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.26.0",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.25.2",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.25.1",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.25.0",
    date: "2026-05-12",
    sections: [{ kind: "security" }],
  },
  {
    version: "1.24.0",
    date: "2026-05-12",
    sections: [
      {
        kind: "feature",
        items: [
          "AI weryfikuje zdjęcia-dowody i pokazuje werdykt rodzicowi w Approvals — ale od teraz każde zatwierdzenie wymaga ręcznej decyzji. Auto-zatwierdzanie zostało wyłączone na stałe.",
          "Limit AI dla rodziny: 100 sugestii zadań / miesiąc. Po wyczerpaniu można tworzyć obowiązki ręcznie bez asysty AI.",
        ],
      },
      { kind: "security" },
    ],
  },
  {
    version: "1.23.0",
    date: "2026-05-12",
    sections: [
      {
        kind: "feature",
        items: [
          "Nowe zadanie i propozycja zadania od dziecka domyślnie startują z zakładką 💛 Pomoc rodzinie zamiast 💰 Za kieszonkowe — zgodnie z filozofią Sianko pomoc rodzinie jest ważniejsza niż zarobek",
        ],
      },
    ],
  },
  {
    version: "1.22.0",
    date: "2026-05-11",
    sections: [
      {
        kind: "bugfix",
        items: [
          "Powiadomienia push znów działają. Wcześniej token FCM gubił się po jakimś czasie (rotacja w przeglądarce) i pushy cicho przestawały przychodzić — teraz przy każdym otwarciu aplikacji token odświeża się automatycznie",
          "Liczba na ikonie aplikacji (badge) prawidłowo maleje po zatwierdzeniu zgłoszenia. Wcześniej zostawała przybita na ostatniej wartości aż do otwarcia appki — szczególnie dla drugiego rodzica i przy AI auto-zatwierdzeniu",
          "Powiadomienia push pokazują teraz właściwą ikonę aplikacji na Androidzie (wcześniej fallback na generic ikonę bo SVG nie był reliable)",
          "Service Worker pewniej ustawia badge na home-screen iOS — wcześniej iOS mógł terminować SW zanim badge faktycznie się zaktualizował",
        ],
      },
    ],
  },
  {
    version: "1.21.0",
    date: "2026-05-11",
    sections: [
      {
        kind: "feature",
        items: [
          "Lista zadań rodzica wygląda teraz tak samo bogato jak u dziecka — duża ikona, font-display tytuł, znacznik 💛 (pomoc rodzinie) lub 💰 (zadanie dodatkowe) zawsze przy nazwie. Family chore na miodowym tle, extra na białym",
          "Zadania pogrupowane po rodzaju: najpierw 💛 Pomoc rodzinie, potem 💰 Zadania dodatkowe — każda sekcja z licznikiem",
          "Filtr statusu w liście zadań: chipy Wszystkie / Do zrobienia / Wykonane. Rodzic widzi prawdziwe wykonanie z całej rodziny — kiedy dziecko zaliczy Spacer z psem, karta od razu ma ✓ i pokazuje kto wykonał",
          "Zadania zatwierdzone przez AI mają wyraźne oznaczenie AI auto-zatwierdziło z procentem pewności i krótkim komentarzem dlaczego AI uznało, że zdjęcie pasuje (np. Widać posprzątaną kuwetę i czystą podłogę)",
        ],
      },
    ],
  },
  {
    version: "1.20.0",
    date: "2026-05-11",
    sections: [
      {
        kind: "feature",
        items: [
          "XP za zadania zależy teraz od wysiłku. Sianko AI sam ocenia trudność (krótkie 15 min vs. większe 15–45 min vs. wymagające ponad 45 min) i przyznaje proporcjonalnie więcej XP. Pomoc rodzinie wciąż daje dwa razy więcej XP niż zadanie dodatkowe — koszenie trawy nie jest już warte tyle samo co wyniesienie śmieci",
          "Tabela: pomoc rodzinie daje 10/20/30 XP (S/M/L), zadanie dodatkowe 5/10/15 XP. Stare zadania, dla których AI jeszcze nie zgadł rozmiaru, liczą się jako średnie (M)",
        ],
      },
    ],
  },
  {
    version: "1.19.0",
    date: "2026-05-11",
    sections: [
      {
        kind: "feature",
        items: [
          "Plan tygodnia (rodzic i dziecko) — nowy układ: zadania pogrupowane w sekcje 💛 Pomoc rodzinie / 💰 Zadania dodatkowe z kolorowym tłem i etykietą sumy (np. 3 zadania, 45 XP). Każde dziecko to osobny accordion — klik w imię zwija/rozwija kartę",
          "Plan tygodnia pokazuje 5 stanów dnia: zatwierdzone, czeka, planowane, nie tego dnia, i dziś (zielona obwódka). Codzienne zadania (np. Spacer z psem) pokazują wyraźnie cały tydzień planu",
          "Plan tygodnia dostępny też dla dziecka — widzi tylko swój plan (bez listy rodzeństwa, bez przycisków edycji)",
          "Cel rodziny zawsze pod celem osobistym na dashboardzie — slim pasek collapsible. Już nie kompaktowy chip w hero, ale stała sekcja",
          "Zadania (lista) — pokazują tylko bieżący dzień. Reszta tygodnia w widoku Plan tygodnia. Bez filtrów Wszystkie / Do zrobienia / Czekają / Zrobione — zostało to czego rzeczywiście dziś trzeba",
          "Propozycje zadań od dziecka: zatwierdzone znikają z listy od razu, odrzucone same usuwają się po 48h",
          "Ustawienia w jednym akordeonie (Wygląd · Propozycje artykułów · Aplikacja · Powiadomienia · AI weryfikacja zdjęć) — jeden klik chowa lub pokazuje wszystkie szczegóły",
          "Trwałe usunięcie konta rodziny (Ustawienia → Usuń konto rodziny). Wymaga podania pseudonimu rodziny i hasła. Imiona, emaile, zdjęcia-dowody są usuwane. Zostają zanonimizowane statystyki (kwoty, typy zadań, cele) — możliwe do agregacji w przyszłości. Operacja nieodwracalna",
          "Dashboard dziecka — przy zadaniach dodatkowych widać teraz 💰 (a przy pomocy rodzinie jak dotąd 💛). Łatwiej rozróżnić typ zadania",
          "„Czas poza ekranem 🌳” → „Aktywność poza ekranem 🌳” — w stronie powitalnej, kartach celów rodzinnych i podsumowaniu tygodnia. Liczymy ilość aktywności poza ekranem, nie czas — nazwa teraz to oddaje",
        ],
      },
      {
        kind: "bugfix",
        items: [
          "Streak tarczy (passa) — naprawiony błąd, przez który tarcza ratowała stare dni z miesięcy wstecz, sztucznie wydłużając passę bez końca. Teraz tarcza działa tylko dla luki w bieżącym tygodniu. Fałszywe uratowania z poprzednich tygodni zostały wyczyszczone — pokazywany streak odpowiada teraz rzeczywistym aktywnościom",
          "Stare cele indywidualne, zapisane przed wprowadzeniem celów rodzinnych, można już znów edytować i usuwać (wcześniej leciał błąd 'Missing or insufficient permissions')",
        ],
      },
    ],
  },
  {
    version: "1.18.0",
    date: "2026-05-11",
    sections: [
      {
        kind: "feature",
        items: [
          "Cel rodziny na dashboardzie zaczyna się zwinięty — slim pasek postępu z tytułem i progresem. Tap rozwija pełną kartę z breakdownem kto ile zrobił i edycją. Mniej miejsca na ekranie, ten sam podgląd postępu",
        ],
      },
    ],
  },
  {
    version: "1.17.0",
    date: "2026-05-10",
    sections: [
      {
        kind: "feature",
        items: [
          "Sianko ma więcej min — 9 nowych póz dziecka (m.in. konfetti, miotełka, medal, wrzucanie monety, ziewanie, na zewnątrz, czekanie, zamyślona) i 11 nowych póz rodzica (kawa, kciuk, relaks, clipboard, żartobliwie przytłoczony, spokojna mama z sercem, dumna mama). Mniej powtarzalności, więcej dopasowania do momentu",
          "Maskotka rodzica zmienia pozę zależnie od kontekstu — rano z kawą, wieczorem zrelaksowana, gdy kolejka zgłoszeń to clipboard, a gdy bardzo dużo dzieciakowego ruchu — żartobliwie przytłoczony tata albo dumna mama z dłońmi przy sercu",
          "Pusty dzień (brak zadań) — Sianko biegnie na zewnątrz zamiast smutno siedzieć. Lepiej pasuje do „Czas na świeże powietrze 🌳”",
          "Gdy dziecko zgłosi wszystkie zadania dnia i czeka na zatwierdzenie — Sianko siedzi cierpliwie z mini-tekstem „Czeka 🕒”. Wcześniej wyglądało jakby trzeba było jeszcze coś zrobić",
        ],
      },
    ],
  },
  {
    version: "1.16.0",
    date: "2026-05-10",
    sections: [
      {
        kind: "feature",
        items: [
          "Cele rodziny 💛 i 🌳 — możecie razem zbierać zadania pomocy rodzinie albo aktywność poza ekranem na wspólną nagrodę. Pasek postępu aktualizuje się sam, gdy rodzic zatwierdza zadania, a karta pokazuje kto ile dorzucił. Cele indywidualne na własną kasę zostają bez zmian — rower ode mnie dla mnie i wycieczka dla całej rodziny żyją obok siebie",
        ],
      },
    ],
  },
  {
    version: "1.15.0",
    date: "2026-05-10",
    sections: [
      {
        kind: "feature",
        items: [
          "Niedzielne planowanie tygodnia — w niedzielę i poniedziałek do 9:00 na ekranie głównym pojawia się drugi banner „Zaplanuj tydzień razem” oraz akcent przycisku na liście Zadań. Klik otwiera kartę z paskiem 7 dni (kropki pokazują które dni mają już zadania), pomysłem na tydzień i listą propozycji od dzieci — klik propozycji prowadzi do zatwierdzeń",
          "Cel rodziny w skarbonce — rodzic może ustawić wspólny cel (np. „Rowerowy weekend”), z paskiem postępu i ikoną. Postęp aktualizujesz ręcznie przy rodzinnej rozmowie. Pojawia się jako fioletowa karta między saldem a listą dzieci",
          "Pytanie tygodnia w podsumowaniu piątkowym — na końcu karty „Tydzień się kończy” pojawia się jedno pytanie zachęcające do rozmowy o pieniądzach (np. „Na co najchętniej teraz odkładasz?”, „Czym różni się fajne od potrzebne?”). „Inne pytanie” miesza wybór, „OK, pogadamy” chowa kartę do końca tygodnia. Zero gamifikacji — Sianko nie zlicza odpowiedzi",
          "Gdy oba bannery rytuału są aktywne (niedziela), pokazują się obok siebie w jednej linii — kompaktowy widok zamiast dwóch szerokich pasków",
          "W karcie „Zaplanujmy tydzień” można przewijać między tygodniami strzałkami — można popatrzeć na poprzedni lub następny tydzień, żeby zobaczyć rozkład zadań",
          "Klik w dzień w karcie planowania rozwija listę zadań tego dnia + przycisk „Dodaj zadanie na [dzień]”. Mini-formularz z polami: nazwa, rodzaj (pomoc rodzinie / za stawkę), kogo dotyczy. Pełne opcje (zdjęcie, opis, fundator) edytujesz później przez Zadania",
          "Pływający przycisk „Dodaj zadanie” zostaje zawsze widoczny w prawym dolnym rogu karty planowania — przy długiej liście zadań nie trzeba scrollować z powrotem do przycisku",
          "Dni które już minęły (np. wczoraj, przedwczoraj) są wyszarzone i nieklikalne w karcie planowania — żeby przez przypadek nie planować przeszłości. Bieżący dzień ma delikatny żółty akcent zamiast intensywnego zielonego, żeby nie konkurował wizualnie z obramówką wybranego dnia",
          "Mini-formularz „Dodaj zadanie” w karcie planowania ma teraz przycisk „✨ AI” — wpisz nazwę, kliknij AI, a Sianko dobierze ikonę i sugerowaną stawkę (jak w pełnym formularzu zadań)",
          "Pytań tygodnia dla rodzinnych rozmów: rozszerzone z 12 do 62 — ponad rok bez powtórki. Tematy: wartość pieniędzy, odraczanie gratyfikacji, dawanie i wspólnota, praca i sprawczość, aktywność poza ekranem, posiadanie i nadmiar, marzenia, relacje. Sianko dobiera deterministycznie pytanie do tygodnia",
        ],
      },
    ],
  },
  {
    version: "1.14.0",
    date: "2026-05-10",
    sections: [
      {
        kind: "feature",
        items: [
          "Podsumowanie tygodnia — od piątku wieczorem do końca niedzieli na ekranie głównym pojawia się dyskretny baner „Tydzień się kończy” (w piątek: „Piątek — weekendu początek”). Klik otwiera kartę z liczbami: ile zarobione razem, ile pomocy rodzinie per dziecko, ile aktywności poza ekranem, top moment tygodnia i postęp celów oszczędzania",
          "Karta podsumowania porównuje zarobki tego tygodnia z poprzednim („+12 zł vs zeszły”) i kończy się delikatną zachętą „Pogadajcie o tym przy obiedzie” — Sianko ma być scaffoldingiem rozmowy, nie jej zamiennikiem",
          "Niedzielny rytuał planowania — w niedzielę i w poniedziałek do 9:00 rano przycisk „Plan tygodnia” na liście zadań zamienia się na zielony „Zaplanuj tydzień razem”, zachęcając do wspólnego ułożenia tygodnia z dziećmi",
        ],
      },
    ],
  },
  {
    version: "1.13.0",
    date: "2026-05-09",
    sections: [
      {
        kind: "feature",
        items: [
          "Instrukcja instalacji jako apka — krok po kroku z mockupami dla iPhone (Safari) i Android (Chrome). Dostępna z ekranów logowania i rejestracji (dyskretny pasek u góry) oraz z Ustawień (sekcja „Aplikacja”)",
          "Wykrywanie czy Sianko działa już jako zainstalowana apka — wtedy pokazujemy „✓ Zainstalowane” zamiast instrukcji",
          "Strona powitalna przepisana zgodnie z filozofią Sianko — wyraźne rozróżnienie pomocy rodzinie (bez stawki, za XP) od zadań dodatkowych (z kieszonkowym), nowy punkt „Aktywność poza ekranem 🌳”",
          "Cała rodzina Sianków na ekranie powitalnym — Tata i Mama wskazują na hero copy z prawego górnego rogu, Sianko junior podskakuje z radości w prawym dolnym",
          "Więcej artykułów w przypominajce Mamy/Taty Sianko — biblioteka rozszerzona do 8 tekstów (m.in. rodzinne hasło na deepfake, magia karty płatniczej, test pianki, rodzic jako pierwsza książka, płacenie za oceny). Maskotka rotuje po całej liście, nie zapętla tego samego artykułu",
        ],
      },
    ],
  },
  {
    version: "1.12.0",
    date: "2026-05-09",
    sections: [
      {
        kind: "feature",
        items: [
          "Cała rodzina expieni — rodzice też mają poziom i XP. Robią zadania dla rodziny (zawsze za XP, nigdy za pieniądze)",
          "Pill poziomu w pasku górnym jako pasek postępu — wypełnia się w miarę zdobywania XP",
          "Klik w pill poziomu otwiera Trofea, kolejny klik wraca do startu",
          "Maskotka rodzica raz na ~7 dni promuje świeży artykuł z bloga sianko.pl — kliknięcie otwiera tekst",
          "Multi-goal: dziecko może mieć kilka celów oszczędzania jednocześnie. Kropki + swipe między nimi w hero, lista wszystkich w skarbonce",
          "Dodawanie kolejnych celów: przycisk Dodaj kolejny cel w skarbonce dziecka",
          "Hero pokazuje cel najbliższy do osiągnięcia (najbardziej motywujący)",
          "Sekcja Twoje zadania dla rodzica na dashboardzie — z przyciskiem Zrób i nagrodą jako XP",
          "Tworzenie zadania na 1 ekranie mobile — kafelki zamiast dropdownów, kalendarz w sheecie, dock 4 opcji jak w iOS",
          "Propozycja zadania (dziecko) ma teraz spójny layout sheet — kafelki częstotliwości, kalendarz, opis collapse",
          "Picker dziecka w sekcji Komu — okrągłe awatary spójne z listą zadań",
          "Lista chores: do zrobienia na górze, zrobione wyszarzone na dole",
          "Karty per-dziecko w skarbonce rodzica jako accordion — domyślnie zwinięte, klik rozwija listę zadań tygodnia",
          "Brand: nowy odcień zielonego (moss) — wszystkie przyciski, ikony, logo",
          "Ikony zadań w stylu iOS-dock (squircle) zamiast prostokątów",
        ],
      },
      {
        kind: "bugfix",
        items: [
          "Cele oszczędzania nie wyświetlały się przez brakujący indeks bazy — poprawione",
          "Pole opisu w propozycji zadania było ucięte przez ujemny margin — poprawione",
          "Czarny accent Wszyscy w Komu zamieniony na kolor brand (moss)",
          "Trybik ustawień w pasku górnym usunięty (duplikat zakładki Ja)",
        ],
      },
    ],
  },
  {
    version: "1.11.0",
    date: "2026-05-09",
    sections: [
      {
        kind: "feature",
        items: [
          "Lista zadań na ekranie dziecka pokazuje wszystkie zadania — zrobione zostają widoczne na dole, dziecko widzi ile już zaliczyło",
          "Nagłówek listy zadań ma licznik zrobionych (np. 2/8) — postęp dnia widać od razu",
          "Skrót 🎯 Wybierz cel pojawia się pod skarbonką, gdy dziecko nie ma jeszcze celu — bez chowania CTA pod listą zadań",
          "Zatwierdzone zadania są wyszarzone z przekreśleniem — wyraźnie odróżnia się od tego, co zostało do zrobienia",
          "Karta tygodniowa rodzica per dziecko: lista zatwierdzonych zadań z dniem tygodnia i nagrodą + przyciski Przesuń (na przyszły tydzień) i Wypłata",
          "Maskotka Sianko mówi różnymi tekstami zależnie od dnia i kontekstu (cel, postęp zadań, pełna skarbonka) — codziennie inny dymek",
          "Maskotka zmienia pozę dopasowaną do kontekstu: skupiona przy zadaniach, dumna po komplecie, ze skarbonką w portfelu, świętuje przy zdobytym celu",
        ],
      },
      {
        kind: "bugfix",
        items: [
          "Cele oszczędzania nie wyświetlały się po zapisaniu — naprawione",
        ],
      },
    ],
  },
  {
    version: "1.10.2",
    date: "2026-05-09",
    sections: [
      {
        kind: "security",
      },
    ],
  },
  {
    version: "1.10.1",
    date: "2026-05-09",
    sections: [
      {
        kind: "bugfix",
        items: [
          "Wylogowanie na wspólnym urządzeniu — kolejny użytkownik nie dostaje już powiadomień poprzedniego",
          "Stawka zadania ograniczona do 10 000 zł — chroni przed literówkami przy wpisywaniu kwot",
          "Krótszy komunikat gdy nazwa użytkownika jest już zajęta",
        ],
      },
    ],
  },
  {
    version: "1.10.0",
    date: "2026-05-09",
    sections: [
      {
        kind: "security",
      },
    ],
  },
  {
    version: "1.9.0",
    date: "2026-05-09",
    sections: [
      {
        kind: "security",
      },
    ],
  },
  {
    version: "1.8.0",
    date: "2026-05-09",
    sections: [
      {
        kind: "feature",
        items: [
          "Czerwona liczba na ikonie aplikacji (iPhone / Android home-screen) z liczbą zadań do zatwierdzenia",
          "Czerwony licznik na ikonie „Sprawdź” w dolnym pasku — widać kiedy dziecko coś zgłosiło",
          "Powitalny wybór roli założyciela: „Jestem Mamą” / „Jestem Tatą” z maskotkami",
          "Filtr dzieci na liście zadań jako kolorowe kółka avatara (każde dziecko ma swój kolor)",
          "Imiona automatycznie zapisywane z wielkiej litery — „kasia” → „Kasia”, „anna-maria” → „Anna-Maria”",
          "„+ Dodaj” w sekcji Dzieci na pulpicie otwiera modal od razu, bez przekierowania",
          "Wybór relacji (Mama / Tata / Babcia… / Syn / Córka) jako lista rozwijana, bez ręcznego wpisywania",
          "„Zapisz profil” w edycji domownika przeniesiony na koniec, nad „Usuń”",
          "„Wyjdź na dwór 🌳” w komunikacie po skończonych zadaniach dziecka",
        ],
      },
      {
        kind: "bugfix",
        items: [
          "Rodzic znowu może zatwierdzać zadania dzieci (regresja po wprowadzeniu uprawnień admin)",
          "Migotanie kafelków „+ Dodaj”, „AI weryfikacja zdjęć”, „Pseudonim rodziny” na koncie założyciela",
          "Filozofia aplikacji nie pojawia się drugi raz po kliknięciu „Rozumiem”",
          "Pole „Imię” w edycji domownika było zasłonięte u góry przy długim formularzu",
        ],
      },
    ],
  },
  {
    version: "1.7.0",
    date: "2026-05-08",
    sections: [
      {
        kind: "feature",
        items: [
          "Filozofia rodzica jako 2-krokowa rozmowa Mama+Tata z dymkami",
          "Nowy avatar rodzica w Ustawieniach (głowa + klatka, bez pikselozy)",
          "Empty-state dashboardu: tata/mama wskazuje palcem na „Załóż konto dziecku”, modal otwiera się inline",
          "Karta „Załóż konto dziecku” na górze dashboardu, nad „Czeka na ciebie”",
          "Edycja profilu: dropdown z relacjami zamiast wolnego pola",
          "Edycja profilu: opcja usunięcia dziecka/opiekuna",
        ],
      },
      {
        kind: "bugfix",
        items: [
          "„Wróć” w Regulaminie i Polityce wraca do poprzedniej strony, nie na home",
          "Hero w Ustawieniach nie psuje się, gdy relacja jest pusta",
          "Self-heal: rodzic bez dokumentu w „Rodzinie” dostaje go automatycznie",
        ],
      },
    ],
  },
];
